import { takeLatest, call, put, fork, select, debounce } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { toInteger } from 'lodash';
import {
	GET_ORDER_GROUP_INFO,
	setOrderGroupInfoAction,
	setOrderGroupInfoLoadingAction,
	DELETE_ORDER_GROUP,
	setIsOrderGroupDeletingAction,
	CREATE_ORDER_GROUP,
	setIsOrderGroupSavingAction,
	GET_TYPES,
	setIsTypesLoadingAction,
	setTypesAction,
	GET_MODES,
	setIsModesLoadingAction,
	setModesAction,
	GET_PERIODS,
	setIsPeriodsLoadingAction,
	setPeriodsAction,
	GET_ADDRESSES,
	setIsAddressesLoadingAction,
	setAddressesAction,
	UPDATE_ORDER_GROUP,
	setAddressesSearchValueAction,
	setIsConfigsLoadingAction,
	setConfigsAction,
	setIsTransportTypesLoadingAction,
	setTransportTypesAction,
	GET_CONFIGS,
	GET_TRANSPORT_TYPES,
	setIsAutoparksLoadingAction,
	setAutoparksAction,
	setIsTariffTypesLoadingAction,
	setTariffTypesAction,
	GET_TARIFF_TYPES,
	GET_AUTOPARKS,
} from './actions';
import {
	getOrderGroupInfoRequest,
	deleteOrderGroupRequest,
	createOrderGroupRequest,
	getOrderGroupsTypesDictionaryRequest,
	getModesDictionaryRequest,
	getPeriodsDictionaryRequest,
	getAddressesBySearchRequest,
	updateOrderGroupRequest,
	getVeerouteConfigsRequest,
	getVeerouteTransportTypesRequest,
	getAutoParkTransportTariffTypesRequest,
	getAutoparksRequest,
} from '../../api/requests';
import { errorHandler } from '../../api/utils';
import {
	getGroupsListAction,
	getCollectionGroupListAction,
	getWaysListAction,
	getGroupsWithWaysListAction,
} from '../orders-groups/actions';
import { DISMISS_TIME } from '../../api/constants';
import { notificationInit } from '../../modules/notifications/actions';
import {
	getAddressesSelector,
	getOrderGroupFormDataForApiSelector,
	getOrderGroupFormIsValidSelector,
	getIsRegularSelector,
} from './selectors';
import { FORM_NAME, REQUEST_ERROR_MESSAGE, INDIVIDUAL_FORM_FIELDS, REGULAR_ROUTE_REQUIRED_FIELDS } from './constants';
import { transformToValidationErrors } from './utils';
import { getGroupOpenSelector } from '../orders-groups/selectors';
import { getGroupsAction, getOrdersListAction } from '../orders/actions';

function* getOrderGroupInfoSaga(action) {
	try {
		yield put(setOrderGroupInfoLoadingAction(true));
		const { data, message, toast } = yield call(getOrderGroupInfoRequest, action.payload);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const addressSearchValues = {};

			const addressesOptions = {};

			if (data.points && data.points.length > 0) {
				const addressFrom = data.points.filter(el => el.type === 'from').map(point => point.address);
				for (let index = 0; index < addressFrom.length; index++) {
					const point = addressFrom[index];
					addressSearchValues[`address_from[${index}].address`] = point;
					const addresses = yield call(getAddressesBySearchRequest, point);
					if (addresses) {
						addressesOptions[`address_from[${index}].address`] = addresses.suggestions;
					}
				}

				const addressTo = data.points.filter(el => el.type === 'to').map(point => point.address);
				for (let index = 0; index < addressTo.length; index++) {
					const point = addressTo[index];
					addressSearchValues[`address_to[${index}].address`] = point;
					const addresses = yield call(getAddressesBySearchRequest, point);
					if (addresses) {
						addressesOptions[`address_to[${index}].address`] = addresses.suggestions;
					}
				}
			}

			yield put(setAddressesAction(addressesOptions));
			yield put(setAddressesSearchValueAction(addressSearchValues));
			yield put(setOrderGroupInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (e) {
		yield fork(errorHandler, e);
	} finally {
		yield put(setOrderGroupInfoLoadingAction(false));
	}
}

function* deleteOrderGroupSaga({ payload: { id, redirect, searchQuery, pathname } }) {
	try {
		yield put(setIsOrderGroupDeletingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const { data, message, toast } = yield call(deleteOrderGroupRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			if (pathname.includes('orders_groups')) {
				yield put(getGroupsListAction({ searchQuery, noLoading: true }));
				yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));
				yield put(getWaysListAction());

				if (groupOpen && groupOpen.id !== toInteger(id)) {
					yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));
				}
			} else if (pathname.includes('orders')) {
				yield put(getOrdersListAction({ searchQuery, noLoading: true }));
				yield put(getGroupsAction());
			}

			redirect();
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrderGroupDeletingAction(false));
	}
}

export function* createOrderGroupSaga({ payload: { redirect, searchQuery } }) {
	try {
		yield put(setIsOrderGroupSavingAction(true));

		const isInvalid = yield select(getOrderGroupFormIsValidSelector());

		if (isInvalid) return;

		const isRegular = yield select(getIsRegularSelector());

		const requiredFields = isRegular ? REGULAR_ROUTE_REQUIRED_FIELDS : INDIVIDUAL_FORM_FIELDS;

		const dataForSave = yield select(getOrderGroupFormDataForApiSelector());

		const { data, errors, message, toast } = yield call(createOrderGroupRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}
			yield put(getGroupsListAction({ searchQuery, noLoading: true }));
			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			redirect();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...requiredFields));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrderGroupSavingAction(false));
	}
}

export function* updateOrderGroupSaga({ payload: { id, redirect, searchQuery, pathname } }) {
	try {
		yield put(setIsOrderGroupSavingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const isInvalid = yield select(getOrderGroupFormIsValidSelector());

		if (isInvalid) return;

		const isRegular = yield select(getIsRegularSelector());

		const requiredFields = isRegular ? REGULAR_ROUTE_REQUIRED_FIELDS : INDIVIDUAL_FORM_FIELDS;

		const dataForSave = yield select(getOrderGroupFormDataForApiSelector());

		const { data, errors, message, toast } = yield call(updateOrderGroupRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(setOrderGroupInfoAction(data));

			if (pathname.includes('orders_groups')) {
				yield put(getGroupsListAction({ searchQuery, noLoading: true }));

				yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));
				if (groupOpen) {
					yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));
				}
			} else if (pathname.includes('orders')) {
				yield put(getOrdersListAction({ searchQuery, noLoading: true }));
				yield put(getGroupsAction());
			}

			redirect();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...requiredFields));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrderGroupSavingAction(false));
	}
}

export function* getTypesSaga() {
	try {
		yield put(setIsTypesLoadingAction(true));

		const { data, message, toast } = yield call(getOrderGroupsTypesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTypesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTypesLoadingAction(false));
	}
}

export function* getConfigsSaga() {
	try {
		yield put(setIsConfigsLoadingAction(true));

		const { data, message, toast } = yield call(getVeerouteConfigsRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setConfigsAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsConfigsLoadingAction(false));
	}
}

export function* getTransportTypesSaga() {
	try {
		yield put(setIsTransportTypesLoadingAction(true));

		const { data, message, toast } = yield call(
			getVeerouteTransportTypesRequest,
			'?order.column=id&order.direct=asc&receive=all',
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTransportTypesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTransportTypesLoadingAction(false));
	}
}

export function* getModesSaga() {
	try {
		yield put(setIsModesLoadingAction(true));

		const { data, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setModesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesLoadingAction(false));
	}
}

export function* getPeriodsSaga() {
	try {
		yield put(setIsPeriodsLoadingAction(true));
		const { data, message, toast } = yield call(getPeriodsDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setPeriodsAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsPeriodsLoadingAction(false));
	}
}

export function* getAddressesSaga({ payload: { searchValue, name } }) {
	try {
		yield put(setIsAddressesLoadingAction({ [name]: true }));

		if (searchValue.length > 255) return;

		const data = yield call(getAddressesBySearchRequest, searchValue);

		if (data?.suggestions) {
			const addresses = yield select(getAddressesSelector());
			yield put(setAddressesAction({ ...addresses, [name]: data.suggestions }));
		} else {
			throw new Error(data?.message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAddressesLoadingAction({ [name]: false }));
	}
}

export function* getAutoparksSaga() {
	try {
		yield put(setIsAutoparksLoadingAction(true));
		const { data, message, toast } = yield call(getAutoparksRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setAutoparksAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparksLoadingAction(false));
	}
}

export function* getTariffTypesSaga() {
	try {
		yield put(setIsTariffTypesLoadingAction(true));
		const { data, message, toast } = yield call(getAutoParkTransportTariffTypesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTariffTypesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTariffTypesLoadingAction(false));
	}
}

export default function* orderGroupsModalSaga() {
	yield takeLatest(GET_ORDER_GROUP_INFO, getOrderGroupInfoSaga);
	yield takeLatest(DELETE_ORDER_GROUP, deleteOrderGroupSaga);
	yield takeLatest(CREATE_ORDER_GROUP, createOrderGroupSaga);
	yield takeLatest(GET_TYPES, getTypesSaga);
	yield takeLatest(GET_MODES, getModesSaga);
	yield takeLatest(GET_PERIODS, getPeriodsSaga);
	yield debounce(1000, GET_ADDRESSES, getAddressesSaga);
	yield takeLatest(UPDATE_ORDER_GROUP, updateOrderGroupSaga);
	yield takeLatest(GET_CONFIGS, getConfigsSaga);
	yield takeLatest(GET_TRANSPORT_TYPES, getTransportTypesSaga);
	yield takeLatest(GET_TARIFF_TYPES, getTariffTypesSaga);
	yield takeLatest(GET_AUTOPARKS, getAutoparksSaga);
}
